import { observer } from 'mobx-react';
import React, { useCallback } from 'react';
import modifiersStore from '../../store/modifiersStore';
import { IModifierOption, IModifierOptionValidation } from '../../types/modifiers';
import { defaultIntegrationTooltipOptions } from 'src/constants/integrations';
import { LOCALES, MEASURE, measureValues } from 'src/constants/main';
import { appStore } from 'src/mobx/appStore';
import integrationsStore from 'src/mobx/integrationStore';
import { ILangTextValidation, TTranslate } from 'src/types/main';
import { booleanNumberReverse, isDisabledByPos } from 'src/utils/helpers';
import { Checkbox } from 'src/view/components/Controls/Checkbox/Checkbox';
import InputAlt from 'src/view/components/Controls/InputAlt/InputAlt';
import MultiLanguageInput from 'src/view/components/Controls/MultiLanguageInput/MultiLanguageInput';
import DeleteRestoreBlock from 'src/view/components/DeleteRestoreBlock/DeleteRestoreBlock';
import IntegrationManager from 'src/view/components/IntegrationManager/IntegrationManager';
// eslint-disable-next-line max-len
import MenuManagTabItemCaloriesInput from 'src/view/screens/MenuManagementScreen/components/MenuManagTabItemCaloriesInput/MenuManagTabItemCaloriesInput';
import MenuManagTabItemPriceInput from 'src/view/screens/MenuManagementScreen/components/MenuManagTabItemPriceInput/MenuManagTabItemPriceInput';
import { getMeasureOption } from '~/helpers';
import { InputWithPickerInside } from '~/view/components/Controls/Inputs';
import { IPickerOption } from '~/view/components/Controls/Pickers/Picker/types';
import './style.scss';

interface IProps {
  option: IModifierOption;
}

const ModifierOption: React.FC<IProps> = observer((props: IProps) => {
  const optionValidation: IModifierOptionValidation | undefined = modifiersStore.validation.options.find((option) => option.id === props.option.id);

  const changeVatValue = useCallback(() => {
    modifiersStore.updateOption(props.option.id, { taxable: booleanNumberReverse(props.option.taxable) });
  }, [props.option]);

  const changeNameValue = useCallback(
    (value: string, currentLanguage: LOCALES) => {
      const name: TTranslate = { ...props.option.name, [currentLanguage]: value };
      modifiersStore.updateOption(props.option.id, { name });
      // update validation
      if (optionValidation && (!optionValidation?.name.enValid || !optionValidation?.name.arValid)) modifiersStore.setValidation();
    },
    [props.option, optionValidation]
  );

  const changeMeasureValue = useCallback(
    (weight: string | undefined) => {
      modifiersStore.updateOption(props.option.id, { weight });
    },
    [props.option]
  );

  const changeMeasureLabel = useCallback(
    (options: IPickerOption[]) => {
      if (!options.length) return;
      modifiersStore.updateOption(props.option.id, { unit: options[0].value as MEASURE });
    },
    [props.option]
  );

  const changePrice = useCallback(
    (price: string) => {
      modifiersStore.updateOption(props.option.id, { price });
      // update validation
      if (optionValidation && !optionValidation.price) modifiersStore.setValidation();
    },
    [props.option, optionValidation]
  );

  const changeCalories = useCallback(
    (calories: number | null) => {
      modifiersStore.updateOption(props.option.id, { calories });
      // update validation
      if (optionValidation && !optionValidation.calories) modifiersStore.setValidation();
    },
    [props.option, optionValidation]
  );

  const changeExternalId = useCallback(
    (value: string | number) => {
      modifiersStore.updateOption(props.option.id, { external_id: value });
    },
    [props.option]
  );

  const handleDelete = useCallback(() => {
    if (appStore.settings?.is_pos) return;
    modifiersStore.deleteOption(props.option.id);
  }, [props.option]);

  const nameValidation = (): ILangTextValidation => {
    return {
      enValid: optionValidation ? optionValidation?.name.enValid : true,
      enErrorText: optionValidation?.name.enErrorText,
      arValid: optionValidation ? optionValidation?.name.arValid : true,
      arErrorText: optionValidation?.name.arErrorText,
    };
  };

  const {
    optionName: nameRestrictionAccess,
    optionsPortion: portionRestrictionAccess,
    optionsCalories: caloriesRestrictionAccess,
    optionGlobalPrice: priceRestrictionAccess,
    deleteOptionFromModifier: deleteRestrictionAccess,
  } = integrationsStore.posRestrictions || {};

  return (
    <li className="modifier-option">
      {appStore.settings?.is_pos ? null : (
        <Checkbox
          value={Boolean(props.option.taxable)}
          name={'modifier-option-' + String(props.option.id)}
          label="txt_vat"
          className="modifier-option__input-vat mr-3 mt-2"
          labelClassName="family-medium text-size-16 color-dark text-uppercase"
          onClick={changeVatValue}
        />
      )}
      {/* name block */}
      <IntegrationManager setting={integrationsStore.posRestrictions?.optionName} withTooltip>
        <MultiLanguageInput
          maxLength={75}
          enText={props.option.name.en}
          arText={props.option.name.ar}
          onChangeValue={changeNameValue}
          validation={nameValidation()}
          tooltip={isDisabledByPos(nameRestrictionAccess) ? defaultIntegrationTooltipOptions : undefined}
          className="modifier-option__input-name mr-3"
        />
      </IntegrationManager>
      {/* portion */}
      <IntegrationManager setting={portionRestrictionAccess} withTooltip>
        <InputWithPickerInside
          type="floatNumber"
          value={props.option.weight}
          onChangeValue={changeMeasureValue}
          disableOnFocusOnInputWrapper
          className="modifier-option__input-portion py-0 mr-3"
          contentClassName="full-width"
          pickerProps={{
            options: measureValues.map(getMeasureOption),
            selectedOptions: [getMeasureOption(props.option.unit)],
            onSelect: changeMeasureLabel,
          }}
        />
      </IntegrationManager>
      {/* calories */}
      <MenuManagTabItemCaloriesInput
        calories={props.option.calories}
        validation={optionValidation?.calories}
        posRestriction={caloriesRestrictionAccess}
        changeCalories={changeCalories}
        setValidation={() => modifiersStore.setValidation()}
        className="modifier-option__input-calories"
        wrapperClassName="mr-3"
      />
      {/* price */}
      <MenuManagTabItemPriceInput
        price={props.option.price}
        validation={optionValidation?.price}
        posRestriction={priceRestrictionAccess}
        changePrice={changePrice}
        setValidation={() => modifiersStore.setValidation()}
        className="modifier-option__input-price"
        wrapperClassName="mr-3"
      />
      {/* external id */}
      <InputAlt value={props.option.external_id} onChangeValue={changeExternalId} className="modifier-option__input-external py-0" />
      {/* delete option button */}
      <IntegrationManager setting={deleteRestrictionAccess} wrapperClassName="ml-4 mt-2" withTooltip>
        <DeleteRestoreBlock disabled={isDisabledByPos(deleteRestrictionAccess)} onDelete={handleDelete} iconClassName="color-gray2" />
      </IntegrationManager>
    </li>
  );
});

export default ModifierOption;
